import { useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  ActivityIndicator,
  Banner,
  Button,
  DrawerActions,
  DrawerContent,
  DrawerHeader,
  HStack,
  Result,
  StatusBadge,
  Tooltip,
  Typography,
  useTheme,
} from "@smartrent/ui";

import { ExclamationCircle } from "@smartrent/icons";

import { OrganizationProps } from "@/types";
import {
  useSmrtCoreSetupMutation,
  useSmrtCoreSetupQuery,
  useSmrtCoreStopMutation,
  SmrtCoreSetupStatus,
} from "@/api/smrt-core-setup";

interface SmrtCoreSetupProps {
  organization: OrganizationProps;
}

type BadgeStatus = "success" | "warning" | "error" | "default";

function statusToBadge(status: SmrtCoreSetupStatus): BadgeStatus {
  switch (status) {
    case "complete":
      return "success";
    case "pending":
      return "warning";
    case "error":
      return "error";
    default:
      return "default";
  }
}

export function SmrtCoreSetup({ organization }: SmrtCoreSetupProps) {
  const { colors } = useTheme();

  const [extraPolls, setExtraPolls] = useState(0);
  const prevStatus = useRef<string | undefined>();
  const lastUpdatedAt = useRef(0);

  const { data, isLoading, isError, dataUpdatedAt } = useSmrtCoreSetupQuery(
    { organizationId: organization.id },
    {
      refetchInterval: (queryData) =>
        queryData?.status === "pending" || extraPolls > 0 ? 3000 : false,
    }
  );

  useEffect(() => {
    if (prevStatus.current === "pending" && data?.status === "complete") {
      setExtraPolls(3);
    }
    prevStatus.current = data?.status;
  }, [data?.status]);

  useEffect(() => {
    if (extraPolls > 0 && dataUpdatedAt > lastUpdatedAt.current) {
      lastUpdatedAt.current = dataUpdatedAt;
      setExtraPolls((p) => p - 1);
    }
  }, [extraPolls, dataUpdatedAt]);

  const [onboard, { isLoading: isMutating }] = useSmrtCoreSetupMutation();
  const [stopOnboarding, { isLoading: isStopping }] = useSmrtCoreStopMutation();

  const isPending = data?.status === "pending";
  const canOnboard = !isLoading && !isMutating && !isStopping && !isPending;

  const entityStats = data?.entities ? Object.entries(data.entities) : [];
  const progressPct =
    data?.overall_progress != null
      ? Math.round(data.overall_progress * 100)
      : 0;

  return (
    <>
      <DrawerHeader title="Smrt-Core Setup" />
      <DrawerContent>
        <View style={styles.content}>
          {!data?.status && !isLoading && !isError && (
            <Banner color="warning" style={styles.banner}>
              {`You are about to enable Smrt-Core for ${organization.name}, this operation cannot be easily undone. Make sure you know what you're doing.`}
            </Banner>
          )}

          {isError ? (
            <Result
              title="Error"
              description="Failed to load Smrt-Core setup status."
              status="error"
              graphic
            />
          ) : (
            <View style={styles.statusRow}>
              <Typography type="bodySemibold" style={styles.statusLabel}>
                Status
              </Typography>
              {isLoading ? (
                <ActivityIndicator />
              ) : (
                <StatusBadge status={statusToBadge(data?.status)}>
                  {data?.status ?? "Not set up"}
                </StatusBadge>
              )}
            </View>
          )}
          {data?.entities && (
            <View style={styles.progressSection}>
              <HStack justify="space-between" align="center">
                <Typography type="bodySemibold">
                  Overall Onboarding Progress
                </Typography>
                <Typography type="body" style={{ color: colors.textSecondary }}>
                  {progressPct}%
                </Typography>
              </HStack>
              <View
                style={[
                  styles.progressBarTrack,
                  { backgroundColor: colors.gray100 },
                ]}
              >
                <View
                  style={[
                    styles.progressBarFill,
                    {
                      width: `${progressPct}%`,
                      backgroundColor: colors.success,
                    },
                  ]}
                />
              </View>

              {entityStats.map(([entity, counts], index) => (
                <View key={entity}>
                  {index > 0 && (
                    <View
                      style={[
                        styles.divider,
                        { backgroundColor: colors.gray100 },
                      ]}
                    />
                  )}
                  <View style={styles.statRow}>
                    <HStack
                      spacing={4}
                      align="center"
                      style={styles.entityNameWrap}
                    >
                      <Typography type="bodySemibold" style={styles.entityName}>
                        {entity.replace(/_/g, " ")}
                      </Typography>
                      {entity === "projects" && (
                        <Tooltip
                          name="smrt-core-projects-tooltip"
                          title="Multi-unit work orders are created as projects with tasks in Work"
                        >
                          <View style={styles.tooltipIcon}>
                            <ExclamationCircle
                              size={14}
                              color={colors.textSecondary}
                            />
                          </View>
                        </Tooltip>
                      )}
                    </HStack>
                    <HStack spacing={0} justify="end">
                      <View style={styles.statCell}>
                        <Typography type="body" style={styles.statNumber}>
                          {counts.created_count}
                        </Typography>
                        <Typography
                          type="caption"
                          style={{ color: colors.textSecondary }}
                        >
                          created
                        </Typography>
                      </View>
                      <View style={styles.statCell}>
                        <Typography type="body" style={styles.statNumber}>
                          {counts.updated_count}
                        </Typography>
                        <Typography
                          type="caption"
                          style={{ color: colors.textSecondary }}
                        >
                          updated
                        </Typography>
                      </View>
                      <View style={styles.statCell}>
                        <Typography type="body" style={styles.statNumber}>
                          {counts.failed_count}
                        </Typography>
                        <Typography
                          type="caption"
                          style={{ color: colors.textSecondary }}
                        >
                          failed
                        </Typography>
                      </View>
                      <View style={styles.statCell}>
                        <Typography type="body" style={styles.statNumber}>
                          {counts.total}
                        </Typography>
                        <Typography
                          type="caption"
                          style={{ color: colors.textSecondary }}
                        >
                          total
                        </Typography>
                      </View>
                    </HStack>
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>
      </DrawerContent>
      <DrawerActions>
        {isPending ? (
          <Button
            disabled={isStopping}
            style={styles.fullWidthButton}
            onPress={() => stopOnboarding({ organizationId: organization.id })}
          >
            {isStopping ? "Stopping..." : "Stop Onboarding"}
          </Button>
        ) : (
          <Button
            disabled={!canOnboard || isMutating}
            style={styles.fullWidthButton}
            onPress={() => onboard({ organizationId: organization.id })}
          >
            {isMutating
              ? "Onboarding..."
              : data?.status === "complete"
                ? "Re-Sync"
                : "Onboard"}
          </Button>
        )}
      </DrawerActions>
    </>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 16,
  },
  banner: {
    marginBottom: 8,
  },
  statusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  statusLabel: {
    marginRight: 4,
  },
  progressSection: {
    gap: 8,
  },
  progressBarTrack: {
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    borderRadius: 4,
  },
  divider: {
    height: 1,
    marginVertical: 4,
  },
  statRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  entityNameWrap: {
    flex: 1,
  },
  entityName: {
    textTransform: "capitalize",
  },
  tooltipIcon: {
    alignSelf: "center",
  },
  statCell: {
    width: 64,
    alignItems: "flex-end",
  },
  statNumber: {
    textAlign: "right",
  },
  fullWidthButton: {
    width: "100%",
  },
});
